import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  RefreshCw, Calendar, X, Phone, Wallet, TrendingUp, Clock3, 
  Search, Edit2, Trash2, DollarSign 
} from 'lucide-react';
import api from '../../services/apiClient.ts';
import { toast } from 'react-toastify';
import { formatDate } from '../../utils/date';
import DateInput from '../common/DateInput';

const API_BASE = '/v1';
const toNumber = (v) => Number(String(v ?? '0').replace(/,/g, ''));
const fmtMoney = (v) => toNumber(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const todayStr = () => new Date().toISOString().slice(0, 10);

// Get date from one year ago
const oneYearAgoStr = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 1);
  return d.toISOString().slice(0, 10);
};

// Convert datetime string to date-only format (YYYY-MM-DD)
const toDateOnly = (dateString) => {
  if (!dateString) return '';
  const d = new Date(dateString);
  if (isNaN(d)) return '';
  const pad = (n) => n.toString().padStart(2, '0');
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  return `${yyyy}-${mm}-${dd}`;
};

// Convert date to SQL format (date only)
const toSqlDate = (v) => {
  if (!v) return null;
  return v; // Already in YYYY-MM-DD format
};

export default function ReceivedPayments() {
  const { t } = useTranslation();

  // Filters - default to last year range and 'all' scope
  const [df, setDf] = useState(oneYearAgoStr());
  const [dt, setDt] = useState(todayStr());
  const [scope, setScope] = useState('all');
  const [groupId, setGroupId] = useState('');
  const [travelerId, setTravelerId] = useState('');
  const [travelerName, setTravelerName] = useState('');

  // Combo data
  const [groups, setGroups] = useState([]);
  const [travelers, setTravelers] = useState([]);

  // Table data
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);

  // Sorting
  const [sort, setSort] = useState('created_at');
  const [dir, setDir] = useState('desc');

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editTravelerName, setEditTravelerName] = useState('');
  const [saving, setSaving] = useState(false);

  const tenant = useMemo(() => localStorage.getItem('tenant') || 'default-tenant-1', []);
  const branch = useMemo(() => localStorage.getItem('branch_id') || '1', []);

  // Setup interceptors
  useEffect(() => {
    const id = api.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      if (token) config.headers.Authorization = `Bearer ${token}`;
      config.headers['X-Tenant'] = tenant;
      config.headers['X-Branch'] = branch;
      return config;
    });
    return () => api.interceptors.request.eject(id);
  }, [tenant, branch]);

  // Fetch combos
  const fetchCombos = async () => {
    try {
      const params = { scope };
      if (df) params.df = df;
      if (dt) params.dt = dt;
      const resp = await api.get('/v1/installmentsQuerycombos', { params });
      setGroups(resp?.data?.groups || []);
      setTravelers(resp?.data?.travelers || []);
    } catch (e) {
      console.error(e);
    }
  };

  // Fetch data
  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        sort,
        dir,
        per_page: perPage,
        page,
      };
      // إضافة الفلاتر فقط إذا كانت موجودة
      if (df) params.df = df;
      if (dt) params.dt = dt;
      if (groupId) params.group_id = groupId;
      if (travelerId) params.traveler_id = travelerId;
      if (travelerName.trim()) params.traveler_name = travelerName.trim();

      const resp = await api.get('/v1/installmentsQuery', { params });
      const payload = resp?.data?.data;
      const items = payload?.data || [];
      setRows(items);
      setPage(payload?.current_page || 1);
      setPerPage(payload?.per_page || 20);
      setLastPage(payload?.last_page || 1);
      setTotal(payload?.total || items.length);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || t('common.errorLoadingData');
      setError(msg);
      setRows([]);
      setLastPage(1);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchCombos();
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload combos when scope/dates change
  useEffect(() => {
    fetchCombos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, df, dt]);

  // Reload data when pagination/sort changes
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, sort, dir]);

  // Calculate stats
  const stats = useMemo(() => {
    let totalAmount = 0;
    rows.forEach((r) => {
      totalAmount += toNumber(r.amount);
    });
    return {
      count: total,
      totalAmount,
    };
  }, [rows, total]);

  // Apply filters
  const applyFilters = () => {
    setPage(1);
    fetchData();
  };

  // Reset filters
  const resetFilters = () => {
    setGroupId('');
    setTravelerId('');
    setTravelerName('');
    setDf(oneYearAgoStr());
    setDt(todayStr());
    setScope('all');
    setPage(1);
    setPerPage(20);
    setSort('created_at');
    setDir('desc');
    setTimeout(() => {
      fetchCombos();
      fetchData();
    }, 0);
  };

  // Toggle sort
  const toggleOrder = (col) => {
    if (sort === col) setDir(dir === 'asc' ? 'desc' : 'asc');
    else {
      setSort(col);
      setDir('asc');
    }
  };

  // Open edit modal
  const openEditModal = async (row) => {
    try {
      const resp = await api.get(`/v1/installmentsQuery/by-entry/${row.id}`);
      const inst = resp?.data?.data?.installment;
      setSelectedRow(row);
      setEditTravelerName(inst?.traveler_name ?? '');
      setEditAmount(inst?.amount ?? 0);
      setEditNotes(inst?.notes ?? '');
      setEditDate(toDateOnly(inst?.created_at ?? null));
      setModalOpen(true);
    } catch (e) {
      console.error(e);
      toast.error(t('common.errorLoadingData') || 'خطأ في جلب البيانات');
    }
  };

  // Save edit
  const saveEdit = async () => {
    if (!selectedRow) return;
    if (!editAmount || toNumber(editAmount) <= 0) {
      toast.error(t('receivedPayments.enterValidAmount') || 'أدخل مبلغ صحيح');
      return;
    }
    if (!editDate) {
      toast.error(t('receivedPayments.selectDate') || 'اختر تاريخ');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        amount: toNumber(editAmount),
        notes: editNotes || null,
        created_at: toSqlDate(editDate),
      };
      await api.put(`/v1/installments/${selectedRow.id}`, payload);
      setModalOpen(false);
      await fetchData();
      toast.success(t('common.savedSuccessfully') || 'تم الحفظ بنجاح ✨');
    } catch (e) {
      toast.error(e?.response?.data?.message || t('common.errorSaving') || 'خطأ أثناء الحفظ');
    } finally {
      setSaving(false);
    }
  };

  // Delete payment
  const handleDelete = async (id) => {
    if (!window.confirm(t('receivedPayments.confirmDelete') || 'هل تريد حذف هذا السند؟')) return;
    try {
      await api.delete(`${API_BASE}/installments/${id}`);
      toast.success(t('common.deletedSuccessfully') || 'تم الحذف');
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('common.errorDeleting') || 'فشل الحذف');
    }
  };

  const header = {
    title: t('receivedPayments.title') || 'كشف المبالغ المستلمة',
    subtitle: t('receivedPayments.subtitle') || 'عرض جميع المبالغ المستلمة من المسافرين (أقساط ونقد)',
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="rounded-2xl bg-gradient-to-r from-primary-600 via-primary-500 to-emerald-500 text-white shadow-lg p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="text-sm opacity-80">{header.subtitle}</p>
            <h1 className="text-2xl font-bold mt-1">{header.title}</h1>
          </div>
          <button
            onClick={() => { fetchCombos(); fetchData(); }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/15 hover:bg-white/25 rounded-xl border border-white/30 transition"
          >
            <RefreshCw className="w-4 h-4" />
            <span>{t('common.refresh') || 'تحديث'}</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <StatCard
          icon={DollarSign}
          title={t('receivedPayments.totalPayments') || 'عدد السندات'}
          value={stats.count}
          tone="from-primary-50 to-primary-100 text-primary-700"
        />
        <StatCard
          icon={Wallet}
          title={t('receivedPayments.totalAmount') || 'إجمالي المبالغ'}
          value={`${fmtMoney(stats.totalAmount)} ${t('common.currencyShort') || 'د.ع'}`}
          tone="from-emerald-50 to-emerald-100 text-emerald-700"
        />
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-soft border border-gray-200 dark:border-gray-800 p-6">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          {/* Scope */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('receivedPayments.scope') || 'النطاق'}
            </label>
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="active">{t('receivedPayments.activeScope') || 'نشط (خلال المدة)'}</option>
              <option value="all">{t('common.all') || 'الكل'}</option>
            </select>
          </div>

          {/* Date From */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('common.fromDate') || 'من تاريخ'}
            </label>
            <div className="relative">
              <DateInput

 value={df}
 onChange={(e) => setDf(e.target.value)}
 className="w-full pl-10 rtl:pl-3 rtl:pr-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-primary-500"
 />
            </div>
          </div>

          {/* Date To */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('common.toDate') || 'إلى تاريخ'}
            </label>
            <div className="relative">
              <DateInput

 value={dt}
 onChange={(e) => setDt(e.target.value)}
 className="w-full pl-10 rtl:pl-3 rtl:pr-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-primary-500"
 />
            </div>
          </div>

          {/* Group */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('common.group') || 'المجموعة'}
            </label>
            <select
              value={groupId}
              onChange={(e) => setGroupId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="">{t('common.all') || 'الكل'}</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
          </div>

          {/* Traveler */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('common.traveler') || 'المسافر'}
            </label>
            <select
              value={travelerId}
              onChange={(e) => setTravelerId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="">{t('common.all') || 'الكل'}</option>
              {travelers.map((tr) => (
                <option key={tr.id} value={tr.id}>{tr.name}</option>
              ))}
            </select>
          </div>

          {/* Traveler Name Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('receivedPayments.searchTraveler') || 'بحث باسم المسافر'}
            </label>
            <div className="relative">
              <Search className="absolute left-3 rtl:left-auto rtl:right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={travelerName}
                onChange={(e) => setTravelerName(e.target.value)}
                placeholder={t('common.search') || 'بحث...'}
                className="w-full pl-10 rtl:pl-3 rtl:pr-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        </div>

        {/* Filter Buttons */}
        <div className="flex justify-end gap-3 mt-4">
          <button
            onClick={resetFilters}
            className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 transition"
          >
            {t('common.reset') || 'إعادة ضبط'}
          </button>
          <button
            onClick={applyFilters}
            className="px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white transition"
          >
            {t('common.apply') || 'تطبيق'}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 rounded-xl bg-rose-50 text-rose-700 border border-rose-100">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-soft border border-gray-200 dark:border-gray-800 overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {t('receivedPayments.tableTitle') || 'سجل المبالغ المستلمة'}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('receivedPayments.tableSubtitle') || 'جميع سندات القبض المسجلة'}
            </p>
          </div>
          {loading && (
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <Clock3 className="w-4 h-4 animate-spin" />
              {t('common.loading') || 'جاري التحميل...'}
            </div>
          )}
        </div>

        <div className="app-table-wrap">
          <table className="app-table min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800/50 text-gray-600 dark:text-gray-300">
              <tr>
                <Th
                  label={t('common.id') || '#'}
                  col="id"
                  sort={sort}
                  dir={dir}
                  onSort={toggleOrder}
                />
                <Th label={t('common.group') || 'المجموعة'} noSort />
                <Th label={t('common.traveler') || 'المسافر'} noSort />
                <Th
                  label={t('common.amount') || 'المبلغ'}
                  col="amount"
                  sort={sort}
                  dir={dir}
                  onSort={toggleOrder}
                  align="right"
                />
                <Th label={t('common.notes') || 'ملاحظات'} noSort />
                <Th
                  label={t('common.date') || 'التاريخ'}
                  col="created_at"
                  sort={sort}
                  dir={dir}
                  onSort={toggleOrder}
                />
                <Th label={t('common.actions') || 'إجراءات'} noSort align="center" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-800 text-gray-900 dark:text-gray-100">
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-6 text-center text-gray-500 dark:text-gray-400">
                    {t('common.noData') || 'لا توجد بيانات'}
                  </td>
                </tr>
              )}
              {rows.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition">
                  <td className="px-4 py-3 text-sm font-medium">{row.id}</td>
                  <td className="px-4 py-3">
                    <div className="font-semibold">{row.group_name || '-'}</div>
                    <div className="text-xs text-gray-500">#{row.group_id}</div>
                  </td>
                  <td className="px-4 py-3 font-medium">{row.traveler_name}</td>
                  <td className="px-4 py-3 text-right font-semibold text-emerald-600 dark:text-emerald-400">
                    {fmtMoney(row.amount)} {t('common.currencyShort') || 'د.ع'}
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                    {row.notes || '-'}
                  </td>
                  <td className="px-4 py-3">
                    {formatDate(row.created_at)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => openEditModal(row)}
                        className="p-2 rounded-lg text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition"
                        title={t('common.edit') || 'تعديل'}
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(row.id)}
                        className="p-2 rounded-lg text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30 transition"
                        title={t('common.delete') || 'حذف'}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Total Row */}
        {rows.length > 0 && (
          <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center">
              <span className="font-semibold text-gray-700 dark:text-gray-300">
                {t('common.total') || 'المجموع'}
              </span>
              <span className="font-bold text-emerald-600 dark:text-emerald-400">
                {fmtMoney(rows.reduce((sum, r) => sum + toNumber(r.amount), 0))} {t('common.currencyShort') || 'د.ع'}
              </span>
            </div>
          </div>
        )}

        {/* Pagination */}
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-4">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {t('common.showing') || 'إجمالي'}: {fmtMoney(total)} | {t('common.page') || 'صفحة'} {page} {t('common.of') || 'من'} {lastPage}
          </div>
          <div className="flex items-center gap-2">
            <button
              className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 transition"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
            >
              {t('common.previous') || 'السابق'}
            </button>
            <button
              className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 transition"
              onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
              disabled={page >= lastPage || loading}
            >
              {t('common.next') || 'التالي'}
            </button>
            <select
              className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded-lg"
              value={perPage}
              onChange={(e) => {
                setPerPage(Number(e.target.value));
                setPage(1);
              }}
            >
              {[10, 20, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}/{t('common.page') || 'صفحة'}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {modalOpen && selectedRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('receivedPayments.editPayment') || 'تعديل السند'}
              </h3>
              <button
                onClick={() => setModalOpen(false)}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition"
              >
                <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              {/* Traveler Name (readonly) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('common.traveler') || 'المسافر'}
                </label>
                <input
                  type="text"
                  value={editTravelerName}
                  disabled
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('common.date') || 'التاريخ'}
                </label>
                <DateInput

 value={editDate}
 onChange={(e) => setEditDate(e.target.value)}
 className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500"
 />
              </div>

              {/* Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('common.amount') || 'المبلغ'}
                </label>
                <input
                  type="text"
                  value={fmtMoney(editAmount)}
                  onChange={(e) => setEditAmount(e.target.value.replace(/,/g, ''))}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 text-right"
                  placeholder="0"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('common.notes') || 'ملاحظات'}
                </label>
                <textarea
                  rows={3}
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500"
                  placeholder={t('common.notesPlaceholder') || 'ملاحظات إضافية...'}
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t dark:border-gray-700">
              <button
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-100 transition"
              >
                {t('common.cancel') || 'إلغاء'}
              </button>
              <button
                onClick={saveEdit}
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white font-semibold transition"
              >
                {saving ? (
                  <>
                    <Clock3 className="w-4 h-4 animate-spin" />
                    {t('common.saving') || 'جاري الحفظ...'}
                  </>
                ) : (
                  t('common.save') || 'حفظ'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Stat Card Component
const StatCard = ({ icon: Icon, title, value, tone }) => (
  <div className={`rounded-2xl border border-gray-100 dark:border-gray-800 bg-gradient-to-br ${tone} p-4 shadow-sm`}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs text-gray-600/80 dark:text-gray-300/80">{title}</p>
        <p className="text-xl font-bold mt-1 text-gray-900 dark:text-gray-50">{value}</p>
      </div>
      <div className="p-2 rounded-xl bg-white/70 dark:bg-gray-900/50 border border-gray-200/60 dark:border-gray-700/60 text-gray-700 dark:text-gray-200">
        <Icon className="w-5 h-5" />
      </div>
    </div>
  </div>
);

// Table Header Component
const Th = ({ label, col, sort, dir, onSort, align, noSort }) => {
  const isActive = sort === col;
  const caret = isActive ? (dir === 'asc' ? '▲' : '▼') : '';
  const alignClass = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-right';

  return (
    <th scope="col" className={`px-4 py-3 font-semibold whitespace-nowrap ${alignClass}`}>
      {noSort ? (
        <span>{label}</span>
      ) : (
        <button
          onClick={() => onSort(col)}
          className="inline-flex items-center gap-1 hover:text-primary-600 transition"
        >
          <span>{label}</span>
          <span className="opacity-60 text-xs">{caret}</span>
        </button>
      )}
    </th>
  );
};
